// Window focus dimming + click-to-front for every floating surface.
//
// A single document-level capture `pointerdown` decides which window the user
// just touched and marks every *other* window `.is-unfocused` (CSS fades it;
// :hover restores full opacity). Touching empty scene / HUD chrome focuses
// nothing, so all windows dim. The focused window is also raised to the top of
// the dynamic z-stack (`raisePane`) — this is what lets a rail-flyout click sit
// above help/console/plot windows, since the flyout is otherwise static z.
//
// Hidden windows never keep the dim class, so one that re-opens later starts
// clean (full opacity) rather than stranded faint.

import { raisePane } from '../draggablePanels';

// Every surface that participates in focus dimming + click-to-front.
const FOCUSABLE =
    '.help-panel, .console-panel, .reductions-plot-panel, .selection-info-panel, .layers-pane, .controls-flyout';
// The rail tab column launches the flyout — a tab click focuses the flyout.
const RAIL = '.controls-rail';
const UNFOCUSED = 'is-unfocused';

export function installWindowFocus(): () => void {
    const panels = (): HTMLElement[] => Array.from(document.querySelectorAll<HTMLElement>(FOCUSABLE));

    const setActive = (active: HTMLElement | null): void => {
        for (const p of panels()) {
            // Clear the dim on the active window and on any hidden window (so a
            // later re-open isn't stranded faint); dim everything else visible.
            const hidden = getComputedStyle(p).display === 'none';
            p.classList.toggle(UNFOCUSED, p !== active && !hidden);
        }
        if (active) raisePane(active);
    };

    const onPointerDown = (e: PointerEvent): void => {
        const t = e.target as Element | null;
        let active = t?.closest(FOCUSABLE) as HTMLElement | null;
        if (!active && t?.closest(RAIL)) {
            active = document.querySelector<HTMLElement>('.controls-flyout');
        }
        setActive(active);
    };

    document.addEventListener('pointerdown', onPointerDown, { capture: true });

    return () => {
        document.removeEventListener(
            'pointerdown',
            onPointerDown,
            // Match the capture-phase registration; lib.dom narrows the 3rd arg to boolean.
            { capture: true } as EventListenerOptions,
        );
        for (const p of panels()) p.classList.remove(UNFOCUSED);
    };
}
